import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch
} from '@mui/material';
import { Plus, Trash2, SquarePen } from 'lucide-react';
import PermissionWrapper from '../../../Components/Auth/PermissionWrapper';
import { getAllDrinks, createDrink, updateDrink, deleteDrink } from '../../../Services/AddRetreatManagement/RetreatDetailsApi';
import DeleteModal from '../../../Components/Modals/DeleteModal';
import { useGlobalSnackbar } from '../../../Components/Common/GlobalSanckbar';


const Drinks = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [drinks, setDrinks] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [formData, setFormData] = useState({
        drinkName: '',
        displayOrder: '1',
        isActive: true
    });
    const [currentItem, setCurrentItem] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const fetchDrinks = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAllDrinks();
            if (response && response.success) {
                setDrinks(response.data);
            } else {
                setError("Failed to load drinks.");
            }
        } catch (err) {
            setError("Failed to fetch drinks. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDrinks();
    }, [fetchDrinks]);

    const handleOpenModal = (item = null) => {
        if (item) {
            setIsEdit(true);
            setCurrentItem(item);
            setFormData({
                drinkName: item.drinkName,
                displayOrder: item.displayOrder !== undefined && item.displayOrder !== null && item.displayOrder !== '' ? String(item.displayOrder) : '1',
                isActive: item.isActive !== undefined ? item.isActive : true
            });
        } else {
            setIsEdit(false);
            setCurrentItem(null);
            setFormData({
                drinkName: '',
                displayOrder: '1',
                isActive: true
            });
        }
        setValidationErrors({});
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEdit(false);
        setCurrentItem(null);
        setFormData({ drinkName: '', displayOrder: '1', isActive: true });
        setValidationErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value, checked, type } = e.target;
        if (name === 'displayOrder') {
            if (value !== "" && (!/^\d+$/.test(value) || Number(value) > 999)) {
                return;
            }
        }
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSubmit = async () => {
        if (!formData.drinkName.trim()) {
            setValidationErrors({ drinkName: 'Drink name is required' });
            return;
        }

        if (formData.displayOrder !== "" && formData.displayOrder !== null && formData.displayOrder !== undefined && (Number(formData.displayOrder) > 999 || Number(formData.displayOrder) < 1)) {
            setValidationErrors({ displayOrder: "Display order must be between 1 and 999." });
            return;
        }

        const payload = {
            drinkName: formData.drinkName,
            isActive: formData.isActive,
            ...(formData.displayOrder !== "" && formData.displayOrder !== null && formData.displayOrder !== undefined ? { displayOrder: Number(formData.displayOrder) } : {})
        };

        try {
            setSubmitting(true);
            setValidationErrors({});
            let response;
            if (isEdit) {
                response = await updateDrink(currentItem.id, payload);
            } else {
                response = await createDrink(payload);
            }
            if (response && response.success) {
                fetchDrinks();
                handleCloseModal();
                showSnackbar(`Drink ${isEdit ? 'updated' : 'created'} successfully!`, 'success');
            } else {
                setValidationErrors({ drinkName: response?.message || 'Failed to save drink' });
            }
        } catch (err) {
            const serverError = err.response?.data;
            const errorMessage = Array.isArray(serverError?.errors)
                ? serverError.errors.join(", ")
                : (serverError?.message || err.message || 'Error saving drink');
            if (serverError && serverError.details) {
                const fieldErrors = {};
                serverError.details.forEach(detail => {
                    const fieldName = detail.field === 'general' ? 'drinkName' : detail.field;
                    fieldErrors[fieldName] = detail.message;
                });
                setValidationErrors(fieldErrors);
            } else {
                setValidationErrors({ drinkName: errorMessage });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            setIsDeleting(true);
            setDeleteError(null);
            const response = await deleteDrink(selectedId);
            if (response && response.success) {
                fetchDrinks();
                setDeleteModalOpen(false);
                setSelectedId(null);
                showSnackbar('Drink deleted successfully!', 'success');
            } else {
                const errorMsg = Array.isArray(response?.errors)
                    ? response.errors.join(", ") : (response?.message || 'Failed to delete drink');
                setDeleteError(errorMsg);
            }
        } catch (err) {
            console.error("Delete Error:", err);
            const serverError = err.response?.data;
            const errorMessage = Array.isArray(serverError?.errors)
                ? serverError.errors.join(", ")
                : (serverError?.message || err.message || 'Error deleting drink');
            setDeleteError(errorMessage);
        } finally {
            setIsDeleting(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Drinks Management
                    </Typography>
                    <PermissionWrapper permission="create" mode="disable">
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                            variant="contained" startIcon={<Plus size={18} />} onClick={() => handleOpenModal()}
                            sx={{
                                bgcolor: '#FFBD65',

                                px: 2.5,
                                color: '#fff',
                                borderRadius: '6px',
                                height: '40px',
                                textTransform: 'none',
                                fontSize: '0.85rem'
                            }}
                        >
                            Add Drink
                        </Button>
                    </PermissionWrapper>
                </Stack>

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 950 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Drink Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Display Order</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px', textAlign: 'right' }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {error ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                            <Typography color="error" sx={{ fontSize: '0.85rem' }}>
                                                {error}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : drinks.length > 0 ? (
                                    drinks.map((drink, index) => (
                                        <TableRow key={drink.id} sx={{
                                            transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{index + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>{drink.drinkName}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666' }}>{drink.displayOrder !== undefined && drink.displayOrder !== null ? drink.displayOrder : 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Box sx={{
                                                    display: 'inline-block',
                                                    px: 1.5, py: 0.3,
                                                    borderRadius: '4px',
                                                    bgcolor: drink.isActive ? '#E8F5E9' : '#FEECEB',
                                                    color: drink.isActive ? '#2E7D32' : '#F44336',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600
                                                }}>
                                                    {drink.isActive ? "Active" : "Inactive"}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {drink.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {drink.uuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(drink.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(drink.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                onClick={() => handleOpenModal(drink)}
                                                                sx={{ color: '#FFBD65' }} size="small"
                                                            >
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                onClick={() => handleDeleteClick(drink.id)}
                                                                sx={{ color: '#FF4D49' }} size="small"
                                                            >
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No drinks found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Create/Edit Modal */}
                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>
                        {isEdit ? 'Edit Drink' : 'Add New Drink'}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={3} sx={{ mt: 2 }}>
                            <TextField
                                fullWidth
                                label="Drink Name"
                                name="drinkName"
                                value={formData.drinkName}
                                onChange={handleInputChange}
                                size="small"
                                required
                                error={!!validationErrors.drinkName}
                                helperText={validationErrors.drinkName}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />
                            <TextField
                                fullWidth
                                type="number"
                                label="Display Order"
                                name="displayOrder"
                                value={formData.displayOrder}
                                onChange={handleInputChange}
                                placeholder="Enter display order (1 - 999)"
                                inputProps={{ min: 1, max: 999, onWheel: (e) => e.target.blur() }}
                                error={Boolean(validationErrors.displayOrder)}
                                helperText={validationErrors.displayOrder}
                                size="small"
                                sx={{
                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                    },
                                    '& input[type=number]': {
                                        MozAppearance: 'textfield',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleInputChange}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFBD65' },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFBD65' }
                                        }}
                                    />
                                }
                                label={<span style={{ fontSize: '0.85rem' }}>Active Status</span>}
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button onClick={handleCloseModal} disabled={submitting} sx={{ color: '#222', textTransform: 'none', fontSize: '0.85rem', px: 2.5 }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            disabled={submitting}
                            sx={{
                                bgcolor: '#FFBD65',

                                color: '#fff',
                                textTransform: 'none',
                                borderRadius: '6px',
                                px: 3,
                                fontSize: '0.85rem'
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update' : 'Create')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Modal */}
                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleDeleteConfirm}
                    title="Delete Drink"
                    description="Are you sure you want to delete this drink? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default Drinks;

